import { Card, Flex, Typography, SimpleTable, Loading } from "djuno-design";
import ExpandableCard from "../components/ExpandableCard";
import { GithubTypesLink } from "./../urls";

const { Title, Text } = Typography;

const LoadingPage = () => {
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Flex direction="col">
        <Flex direction="col">
          <Title level={4}>Loading</Title>
          <Text size="sm" style={{ marginBottom: 30 }}>
            The Loading component in Djuno Design provides an animated indicator
            to signify ongoing operations or loading states. It offers various
            visual styles to match different themes and contexts within your
            application. With customizable options for size, animation type, and
            color themes, this component ensures your users are informed when a
            process is in progress without disrupting the overall user
            experience
          </Text>

          <Title level={5}>Variants and Customization</Title>
          <Text size="sm">
            The Loading component supports multiple animation types:
          </Text>
          <ul className="marker:text-sky-400 list-disc pl-6">
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Simple
                </Text>
                : A standard spinning circle animation.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Cutoff
                </Text>
                : A variation of the spin animation with a sharper cutoff point.
              </Text>
            </li>
            <li>
              <Text size="sm">
                <Text size="sm" strong>
                  Elastic
                </Text>
                : A more dynamic animation that emphasizes stretching and
                rotation.
              </Text>
            </li>
          </ul>
          <Text size="sm" className="mt-4">
            {/* <Text size="sm" strong>
              large
            </Text>{" "} */}
            The component also allows for customization of size, border
            thickness, and themes (such as primary, error, transparent, or
            white), making it adaptable to different use cases and design needs.
          </Text>
        </Flex>

        {/* Examples */}
        <Flex direction="col">
          <Title level={5} className="mt-5">
            Examples
          </Title>
          <Flex direction="col" className="gap-4">
            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="types"
                codeViewerProps={{
                  code: LoadingExample1,
                }}
              >
                <Flex items={{ default: "center" }} className="gap-8">
                  <Flex direction="col" items="center" className="gap-1">
                    <Loading uiType="simple" />
                    <Text>simple</Text>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Loading uiType="cutoff" />
                    <Text>cutoff</Text>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Loading uiType="elastic" />
                    <Text>elastic</Text>
                  </Flex>
                </Flex>
              </ExpandableCard>
            </Flex>

            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="size and borderSize"
                codeViewerProps={{
                  code: LoadingExample2,
                }}
              >
                <Flex items="center" className="gap-8">
                  <Flex direction="col" items="center" className="gap-3">
                    <Loading uiSize={24} borderSize={4} />
                    <Flex direction="col" items="center">
                      <Text size="sm">size: 24</Text>
                      <Text size="sm">borderSize: 4</Text>
                    </Flex>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-3">
                    <Loading uiSize={35} borderSize={2} />
                    <Flex direction="col" items="center">
                      <Text size="sm">size: 35</Text>
                      <Text size="sm">borderSize: 2</Text>
                    </Flex>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-3">
                    <Loading uiSize={1} borderSize={14} />
                    <Flex direction="col" items="center">
                      <Text size="sm">size: 1</Text>
                      <Text size="sm">borderSize: 14</Text>
                    </Flex>
                  </Flex>
                </Flex>
              </ExpandableCard>
            </Flex>

            <Flex direction={{ default: "col", lg: "row" }} className="gap-4">
              <ExpandableCard
                title="theme"
                codeViewerProps={{
                  code: LoadingExample3,
                }}
              >
                <Flex items="center" className="gap-8">
                  <Flex direction="col" items="center" className="gap-1">
                    <Loading theme="primary" />
                    <Text size="sm">primary</Text>
                  </Flex>
                  <Flex direction="col" items="center" className="gap-1">
                    <Loading theme="error" />
                    <Text size="sm">error</Text>
                  </Flex>

                  <Flex
                    direction="col"
                    items="center"
                    className="gap-1 bg-blue-400"
                  >
                    <Loading theme="white" />
                    <Text size="sm">white</Text>
                  </Flex>

                  <Flex direction="col" items="center" className="gap-1">
                    <Loading theme="transparent" />
                    <Text size="sm">transparent</Text>
                  </Flex>
                </Flex>
              </ExpandableCard>
            </Flex>
          </Flex>
        </Flex>

        {/* API */}
        <Flex direction="col" className="w-full mt-5">
          <Title level={5} className="">
            API
          </Title>
          <Flex direction="col" className="gap-1 w-full">
            <Card id="API">
              <SimpleTable>
                <SimpleTable.Head>
                  <SimpleTable.TH>Property</SimpleTable.TH>
                  <SimpleTable.TH>Description</SimpleTable.TH>
                  <SimpleTable.TH>Type</SimpleTable.TH>
                  <SimpleTable.TH>Default</SimpleTable.TH>
                </SimpleTable.Head>
                <SimpleTable.Body>
                  {/* uiType */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiType</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Type of loading indicator</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L23"}
                        size="xs"
                        code
                      >
                        LoadingType
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">simple</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* size */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">uiSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">Size of the loading indicator</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">24px</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* borderSize */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">borderSize</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Size of the loading indicator's border
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="xs" code>
                        number
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">0.125rem</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                  {/* theme */}
                  <SimpleTable.Row>
                    <SimpleTable.TD>
                      <Text size="sm">theme</Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">
                        Theme of the loading indicator, defining its appearance
                      </Text>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Typography.Link
                        target="_blank"
                        href={GithubTypesLink + "Loading.ts#L24"}
                        size="xs"
                        code
                      >
                        LoadingThemeTypes
                      </Typography.Link>
                    </SimpleTable.TD>
                    <SimpleTable.TD>
                      <Text size="sm">primary</Text>
                    </SimpleTable.TD>
                  </SimpleTable.Row>
                </SimpleTable.Body>
              </SimpleTable>
            </Card>
          </Flex>
        </Flex>
      </Flex>
    </div>
  );
};

const LoadingExample1 = `<Flex items={{ default: "center" }} className="gap-8">
  <Flex direction="col" items="center" className="gap-1">
    <Loading type="simple" />
    <Text>simple</Text>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Loading type="cutoff" />
    <Text>cutoff</Text>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Loading type="elastic" />
    <Text>elastic</Text>
  </Flex>
</Flex>`;
const LoadingExample2 = `<Flex items="center" className="gap-8">
  <Flex direction="col" items="center" className="gap-3">
    <Loading size={24} borderSize={4} />
    <Flex direction="col" items="center">
      <Text size="sm">size: 24</Text>
      <Text size="sm">borderSize: 4</Text>
    </Flex>
  </Flex>
  <Flex direction="col" items="center" className="gap-3">
    <Loading size={35} borderSize={2} />
    <Flex direction="col" items="center">
      <Text size="sm">size: 35</Text>
      <Text size="sm">borderSize: 2</Text>
    </Flex>
  </Flex>
  <Flex direction="col" items="center" className="gap-3">
    <Loading size={1} borderSize={14} />
    <Flex direction="col" items="center">
      <Text size="sm">size: 1</Text>
      <Text size="sm">borderSize: 14</Text>
    </Flex>
  </Flex>
</Flex>`;
const LoadingExample3 = `<Flex items="center" className="gap-8">
  <Flex direction="col" items="center" className="gap-1">
    <Loading theme="primary" />
    <Text size="sm">primary</Text>
  </Flex>
  <Flex direction="col" items="center" className="gap-1">
    <Loading theme="error" />
    <Text size="sm">error</Text>
  </Flex>

  <Flex
    direction="col"
    items="center"
    className="gap-1 bg-blue-400"
  >
    <Loading theme="white" />
    <Text size="sm">white</Text>
  </Flex>

  <Flex direction="col" items="center" className="gap-1">
    <Loading theme="transparent" />
    <Text size="sm">transparent</Text>
  </Flex>
</Flex>`;

export default LoadingPage;
